import { useEffect, useRef, useState } from 'react';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

/**
 * Two-factor code entry step.
 *
 * Two-factor authentication is mandatory on the Lynki platform, so this screen is
 * always shown after a correct password. Every user meets it on every login, so its
 * failure modes matter more than most UI. See docs/SECURITY-2FA.md.
 */
export interface TwoFactorFormProps {
  /** Masked destination address shown to the user, e.g. "he****@lynki.no". */
  maskedEmail?: string;
  /** Validity window in minutes, for the helper text. */
  expiresInMinutes?: number;
  /**
   * Whether to offer "husk denne enheten" at all. The server decides (admin policy, and
   * whether this account is an admin that must always type a code); default on.
   */
  canRememberDevice?: boolean;
  /** Trust window for the checkbox label, from the admin policy. */
  rememberDeviceDays?: number;
  /**
   * Verifies the entered code. `rememberDevice` is the state of the "husk denne enheten"
   * checkbox at submit time. Resolves to an error message, or null on success.
   */
  onVerify: (code: string, rememberDevice: boolean) => Promise<string | null>;
  /** Requests a new code. Resolves to a status message to display. */
  onResend: () => Promise<string>;
  /** Returns to the password step. */
  onBack: () => void;
}

/** Codes are six digits; anything else cannot be valid, so we avoid a round trip. */
const CODE_LENGTH = 6;

/** Matches the server-side resend cooldown in TwoFactorService. */
const RESEND_COOLDOWN_SECONDS = 30;

export function TwoFactorForm({
  maskedEmail,
  expiresInMinutes = 3,
  canRememberDevice = true,
  rememberDeviceDays = 90,
  onVerify,
  onResend,
  onBack,
}: TwoFactorFormProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [rememberDevice, setRememberDevice] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Count the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const submit = async (value: string) => {
    if (value.length !== CODE_LENGTH || isVerifying) return;

    setIsVerifying(true);
    setError(null);
    setStatus(null);

    const message = await onVerify(value, canRememberDevice && rememberDevice);

    if (message) {
      setError(message);
      setCode('');
      inputRef.current?.focus();
    }
    setIsVerifying(false);
  };

  const handleChange = (raw: string) => {
    // Keep digits only so pasted codes with spaces or dashes still work.
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    setError(null);

    // Auto-submit as soon as a full code is present, including on paste.
    if (digits.length === CODE_LENGTH) {
      void submit(digits);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    const message = await onResend();
    setStatus(message);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const isComplete = code.length === CODE_LENGTH;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit(code);
      }}
      className="space-y-5"
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <Mail className="w-6 h-6 text-slate-700" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900">Sjekk e-posten din</h2>
        <p className="mt-1 text-sm text-slate-500">
          Vi har sendt en engangskode
          {maskedEmail ? <> til <span className="font-medium text-slate-700">{maskedEmail}</span></> : null}.
          Koden er gyldig i {expiresInMinutes} minutter.
        </p>
      </div>

      {/*
        Above the code input, not below it: the code auto-submits the moment six digits
        are in, so anything placed after the input is never seen before the form is gone.
      */}
      {canRememberDevice && (
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            disabled={isVerifying}
            className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
          />
          Husk denne enheten i {rememberDeviceDays} dager
        </label>
      )}

      <div>
        <label htmlFor="twofactor-code" className="sr-only">
          Engangskode
        </label>
        <input
          ref={inputRef}
          id="twofactor-code"
          name="one-time-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          // No numeric placeholder: "000000" reads as a code that is already filled in,
          // which makes people hesitate or try to submit it. Dashes clearly signal
          // "six characters go here" without looking like data.
          placeholder="------"
          value={code}
          onChange={(e) => handleChange(e.target.value)}
          disabled={isVerifying}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'twofactor-error' : undefined}
          className="w-full text-center text-2xl tracking-[0.5em] font-mono px-4 py-3 border border-slate-300 rounded-lg placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent disabled:bg-slate-50"
        />
      </div>

      {error && (
        <p id="twofactor-error" role="alert" className="text-sm text-red-600 text-center">
          {error}
        </p>
      )}
      {status && !error && (
        <p role="status" className="text-sm text-slate-600 text-center">
          {status}
        </p>
      )}

      {/*
        The disabled state is a light outline rather than a grey fill. A grey fill sits
        too close to the dark enabled style to read as "not yet available", which left
        people clicking a button that does nothing.
      */}
      <button
        type="submit"
        disabled={!isComplete || isVerifying}
        className={[
          'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors',
          isComplete && !isVerifying
            ? 'bg-slate-900 text-white hover:bg-slate-800'
            : 'bg-white text-slate-400 border border-slate-200 cursor-not-allowed',
        ].join(' ')}
      >
        {isVerifying && <Loader2 className="w-4 h-4 animate-spin" />}
        {isVerifying ? 'Verifiserer...' : 'Logg inn'}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Tilbake
        </button>

        {/*
          During the cooldown this is plain text, not a disabled button. A greyed-out
          control invites clicking; a countdown reads as information. It becomes a real
          action only once it can actually do something.
        */}
        {cooldown > 0 ? (
          <span className="text-slate-400" aria-live="polite">
            Ny kode om {cooldown}s
          </span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            className="text-slate-600 underline underline-offset-2 hover:text-slate-900 transition-colors"
          >
            Send ny kode
          </button>
        )}
      </div>
    </form>
  );
}

export default TwoFactorForm;
